import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Mail,
  Phone,
  Building2,
  User as UserIcon,
  Calendar,
  Inbox,
  CheckCircle2,
  Archive,
  Trash2,
  Eye,
  Filter,
  Search,
  X,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  new: { label: "New", color: "bg-sky-100 text-sky-700 border-sky-200", icon: Inbox },
  read: { label: "Read", color: "bg-slate-100 text-slate-700 border-slate-200", icon: Eye },
  responded: { label: "Responded", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  archived: { label: "Archived", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Archive },
};

function StatusBadge({ status }) {
  const cfg = STATUS[status] || STATUS.new;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/leads`, { params });
      setLeads(data);
    } catch (e) {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const filtered = useMemo(() => {
    if (!search) return leads;
    const q = search.toLowerCase();
    return leads.filter(
      (l) =>
        `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.company.toLowerCase().includes(q)
    );
  }, [leads, search]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Marked as ${STATUS[status].label}`);
    } catch (e) {
      toast.error("Failed to update lead.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted.");
    } catch (e) {
      toast.error("Failed to delete lead.");
    }
  };

  const tabs = [
    { key: "all", label: "All" },
    { key: "new", label: "New" },
    { key: "read", label: "Read" },
    { key: "responded", label: "Responded" },
    { key: "archived", label: "Archived" },
  ];

  return (
    <div data-testid="admin-leads-page" className="p-6 lg:p-10">
      <Toaster position="top-right" richColors />

      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Leads Inbox</h1>
          <p className="mt-1 text-sm text-slate-500">Manage demo requests and contact submissions.</p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="leads-refresh"
          className="border-slate-300 text-slate-700"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3 mb-5">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              data-testid={`leads-filter-${t.key}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                filter === t.key
                  ? "bg-sky-600 text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or company..."
            className="pl-10 border-slate-300 bg-white"
            data-testid="leads-search"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading leads...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No leads to show.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((lead) => (
              <li
                key={lead.id}
                data-testid={`lead-row-${lead.id}`}
                onClick={() => {
                  setSelected(lead);
                  if (lead.status === "new") updateStatus(lead.id, "read");
                }}
                className={`cursor-pointer px-5 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4 ${
                  selected?.id === lead.id ? "bg-sky-50/40" : ""
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 text-xs font-bold shrink-0">
                  {lead.first_name?.charAt(0)}{lead.last_name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {lead.first_name} {lead.last_name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{lead.email}</p>
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm text-slate-700 truncate">{lead.company}</p>
                    <p className="text-xs text-slate-400 truncate">{lead.role}</p>
                  </div>
                  <div className="flex items-center justify-end gap-3 shrink-0">
                    <StatusBadge status={lead.status} />
                    <span className="text-xs text-slate-400 hidden md:inline">{formatDate(lead.created_at)}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setSelected(null)}
          />
          <aside
            data-testid="lead-drawer"
            className="ml-auto relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Lead Details</p>
                <h2 className="text-lg font-bold text-slate-900">
                  {selected.first_name} {selected.last_name}
                </h2>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="lead-drawer-close"
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <StatusBadge status={selected.status} />
                <span className="text-xs text-slate-400">{formatDate(selected.created_at)}</span>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Mail className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <a href={`mailto:${selected.email}`} className="text-sky-600 hover:underline break-all">
                    {selected.email}
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <a href={`tel:${selected.phone}`} className="text-slate-700 hover:text-sky-600">
                    {selected.phone}
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-slate-700">{selected.company}</span>
                </div>
                <div className="flex items-start gap-3">
                  <UserIcon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-slate-700">{selected.role}</span>
                </div>
                <div className="flex items-start gap-3">
                  <Filter className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-slate-700">Heard via: <span className="font-medium">{selected.referral_source}</span></span>
                </div>
                {selected.source_page && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <span className="text-slate-700">From page: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{selected.source_page}</code></span>
                  </div>
                )}
              </div>

              {selected.other_info && (
                <div className="border-t border-slate-100 pt-5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Message</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-lg p-3">
                    {selected.other_info}
                  </p>
                </div>
              )}

              <div className="border-t border-slate-100 pt-5 space-y-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Actions</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    data-testid="lead-mark-responded"
                    disabled={selected.status === "responded"}
                    onClick={() => updateStatus(selected.id, "responded")}
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />
                    Mark Responded
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="lead-archive"
                    disabled={selected.status === "archived"}
                    onClick={() => updateStatus(selected.id, "archived")}
                    className="border-amber-200 text-amber-700 hover:bg-amber-50"
                  >
                    <Archive className="h-4 w-4 mr-1.5" />
                    Archive
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="lead-mark-new"
                    disabled={selected.status === "new"}
                    onClick={() => updateStatus(selected.id, "new")}
                  >
                    <Inbox className="h-4 w-4 mr-1.5" />
                    Mark New
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="lead-delete"
                    onClick={() => remove(selected.id)}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Delete
                  </Button>
                </div>
                <a
                  href={`mailto:${selected.email}?subject=RE: Your Antera Demo Request`}
                  className="block mt-3"
                >
                  <Button data-testid="lead-reply" className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                    <Mail className="h-4 w-4 mr-2" />
                    Reply via Email
                  </Button>
                </a>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
